import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlarmClock, Check, Loader2, RefreshCw } from "lucide-react";

type Alert = {
  id: string;
  route_id: string | null;
  route_label: string | null;
  category: string | null;
  threshold: number;
  window_minutes: number;
  failure_count: number;
  triggered_by_failure_id: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  triggered_at: string;
};

export function HelpdeskRouteThresholdAlertsPanel() {
  const [rows, setRows] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAcked, setShowAcked] = useState(false);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("helpdesk_route_threshold_alerts" as any)
      .select("id, route_id, route_label, category, threshold, window_minutes, failure_count, triggered_by_failure_id, acknowledged_at, acknowledged_by, triggered_at")
      .order("triggered_at", { ascending: false })
      .limit(100);
    if (!showAcked) q = q.is("acknowledged_at", null);
    const { data, error } = await q;
    setLoading(false);
    if (error) return toast.error(`Load threshold alerts: ${error.message}`);
    setRows((data ?? []) as unknown as Alert[]);
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showAcked]);

  const ack = async (a: Alert) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("helpdesk_route_threshold_alerts" as any)
      .update({ acknowledged_at: new Date().toISOString(), acknowledged_by: user?.id ?? null })
      .eq("id", a.id);
    if (error) return toast.error(error.message);
    toast.success("Acknowledged");
    void load();
  };

  const openCount = rows.filter((r) => !r.acknowledged_at).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <AlarmClock className={`size-4 ${openCount ? "text-destructive" : "text-muted-foreground"}`} />
            Route failure-threshold alerts
            {openCount > 0 && <Badge variant="destructive">{openCount} new</Badge>}
          </CardTitle>
          <CardDescription>
            Fires when a specific Slack route accumulates more failures than its configured threshold inside its
            rolling window. Configure the threshold per route in the routing panel above.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowAcked((v) => !v)}>
            {showAcked ? "Hide acknowledged" : "Show acknowledged"}
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="size-4 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">
            {showAcked ? "No threshold alerts recorded." : "No open threshold alerts. 🎉"}
          </div>
        ) : (
          <div className="divide-y divide-border/50 text-sm">
            {rows.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 py-2">
                <span className="font-mono text-xs text-muted-foreground">
                  {new Date(a.triggered_at).toLocaleString()}
                </span>
                <span className="font-medium">{a.route_label ?? "(deleted route)"}</span>
                {a.category && <Badge variant="secondary">{a.category}</Badge>}
                <span className="text-xs">
                  <strong className="text-destructive">{a.failure_count}</strong> failures in the last{" "}
                  {a.window_minutes} min · threshold {a.threshold}
                </span>
                <div className="ml-auto">
                  {a.acknowledged_at ? (
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <Check className="size-3.5" /> ack {new Date(a.acknowledged_at).toLocaleTimeString()}
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => void ack(a)}>Acknowledge</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default HelpdeskRouteThresholdAlertsPanel;
